import { useTranslation } from 'react-i18next';
import styles from './SettingsPage.module.css';
import Text from '../../componentsNew/atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../componentsNew/enums';
import { InputField } from '../../componentsNew/organisms/input-field';
import { Data, FormErrors } from '../../componentsNew/molecules/form/Form';
import FormError from '../../componentsNew/molecules/form/FormError';
import { RadioInput } from '../../componentsNew/atoms/radio-input';
import SkeletonComponent from '../../components/SkeletonComponent';

interface GeneralSettingsContentProps {
    formData: Data;
    formErrors: FormErrors[];
    isLoading?: boolean;
}

const GeneralSettingsContent = ({
    formData,
    formErrors,
    isLoading = false
}: GeneralSettingsContentProps) => {
    const { t } = useTranslation();

    const renderAutomaticEnrichmentSection = () => {
        const options = [
            {'label': t('settingsPage.general.automatic.enriching.on'), 'value': 'true'},
            {'label': t('settingsPage.general.automatic.enriching.off'), 'value': 'false'},
        ]

        return <div className={styles.contentSection}>
            <div className={styles.descriptionPanel}>
                <Text
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                    tag='h6'
                >
                    {t('settingsPage.general.automatic.enriching')}
                </Text>
                <Text
                    size={FontSizeEnum.Sm}
                    color={ColorEnum.Gray800}
                    tag='p'
                >
                    {t('settingsPage.general.automatic.enriching.description')}
                </Text>
            </div>
            <div className={styles.settingsPanel}>
                {isLoading
                    ?
                    <div className={styles.radioButtonSkeletonContainer}>
                        <SkeletonComponent height={24} width={70}/>
                        <SkeletonComponent height={24} width={70}/>
                    </div>
                    :
                    <RadioInput
                        required
                        options={options}
                        name={'automaticEnrichment'}
                        value={formData['automaticEnrichment']?.value.toString()}
                    />
                }
                <FormError errors={formErrors['automaticEnrichment'] || []} />
            </div>
        </div>;
    }

    const renderPrivacyPolicyUrlSection = () => {
        return <div className={styles.contentSection}>
            <div className={styles.descriptionPanel}>
                <Text
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                    tag='h6'
                >
                    {t('settingsPage.general.privacy.policy')}
                </Text>
                <Text
                    size={FontSizeEnum.Sm}
                    color={ColorEnum.Gray800}
                    tag='p'
                >
                    {t('settingsPage.general.privacy.policy.description')}
                </Text>
            </div>
            <div className={styles.settingsPanel}>
                {isLoading
                    ?
                    <SkeletonComponent height={40}/>
                    :
                    <InputField
                        required name={'privacyPolicyUrl'}
                        value={formData['privacyPolicyUrl']?.value as string}
                    />
                }
                <FormError errors={formErrors['privacyPolicyUrl'] || []} />
            </div>
        </div>;
    };

    const renderLogoUrlSection = () => {
        return <div className={styles.contentSection}>
            <div className={styles.descriptionPanel}>
                <Text
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                    tag='h6'
                >
                    {t('settingsPage.general.logo.url')}
                </Text>
                <Text
                    size={FontSizeEnum.Sm}
                    color={ColorEnum.Gray800}
                    tag='p'
                >
                    {t('settingsPage.general.logo.url.description')}
                </Text>
            </div>
            <div className={styles.settingsPanel}>
                {isLoading
                    ?
                    <SkeletonComponent height={40}/>
                    :
                    <InputField
                        required
                        name={'logoUrl'}
                        value={formData['logoUrl']?.value as string}
                    />
                }
                <FormError errors={formErrors['logoUrl'] || []} />
            </div>
        </div>;
    };

    return (
        <>
            {renderAutomaticEnrichmentSection()}
            <hr/>
            {renderPrivacyPolicyUrlSection()}
            <hr/>
            {renderLogoUrlSection()}
        </>
    );
};

export default GeneralSettingsContent;
